import { BarcodeOutlined, CreditCardOutlined, QrcodeOutlined } from '@ant-design/icons';
import { Button, Form } from 'antd';
import { useCallback } from 'react';
import Input from '../../Input';
import { useForm } from '../context';
import { moneyMask } from '../helper';
import Boleto from './Boleto';
import CreditCard from './CreditCard';
import CreditCardResumed from './CreditCardResumed';
import Pix from './Pix';

const PaymentMethod = ({ name, field, fields, index }: any) => {
  const { form, checkout, getTotal, paymentMethods } = useForm();
  const { key: fieldKey, ...restField } = field;
  const paymentMethod = Form.useWatch([...name, field.name, 'paymentMethod'], form);
  const creditCardId = Form.useWatch([...name, field.name, 'creditCard', 'id'], form);
  const payments = Form.useWatch(name, form);

  const canShowBoleto =
    paymentMethods.includes('boleto') &&
    (checkout.allowMultiplePaymentsMethods || (!checkout.allowMultiplePaymentsMethods && index === 0));
  const canShowPix =
    paymentMethods.includes('pix') &&
    (checkout.allowMultiplePaymentsMethods || (!checkout.allowMultiplePaymentsMethods && index === 0));
  const canShowCreditCard = paymentMethods.includes('credit_card');

  const hasOnlyOnePaymentMethodShown = [canShowBoleto, canShowPix, canShowCreditCard].filter(Boolean).length === 1;

  const changePaymentMethod = useCallback(
    (method: string) => {
      form.setFieldValue([...name, field.name, 'paymentMethod'], method);
    },
    [form, field.name, name]
  );

  return (
    <>
      {!hasOnlyOnePaymentMethodShown && (
        <div className="pb-4 flex items-center justify-center">
          <p className="font-medium text-gray-600">Selecione uma forma de pagamento</p>
        </div>
      )}

      <Form.Item
        key={fieldKey}
        {...restField}
        name={[field.name, 'paymentMethod']}
        rules={[{ required: true, message: 'Selecione um tipo de pagamento' }]}
        hidden={hasOnlyOnePaymentMethodShown}
      >
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
          {canShowCreditCard && (
            <Button
              color={paymentMethod === 'credit_card' ? 'primary' : 'default'}
              variant='outlined'
              icon={<CreditCardOutlined />}
              block
              onClick={() => changePaymentMethod('credit_card')}
            >
              <span className='font-medium'>Cartão</span>
            </Button>
          )}

          {canShowPix && (
            <Button
              color={paymentMethod === 'pix' ? 'primary' : 'default'}
              variant='outlined'
              icon={<QrcodeOutlined />}
              block
              onClick={() => changePaymentMethod('pix')}
            >
              <span className='font-medium'>PIX</span>
            </Button>
          )}

          {canShowBoleto && (
            <Button
              color={paymentMethod === 'boleto' ? 'primary' : 'default'}
              variant='outlined'
              icon={<BarcodeOutlined />}
              block
              onClick={() => changePaymentMethod('boleto')}
            >
              <span className='font-medium'>Boleto</span>
            </Button>
          )}
        </div>
      </Form.Item>

      <Form.Item name={[field.name, 'forceNewInstallments']} hidden noStyle>
        <Input hidden />
      </Form.Item>

      {fields.length > 1 && (
        <Form.Item
          label="Valor"
          name={[field.name, 'value']}
          rules={[
            {
              required: true,
              validator: async (_, value) => {
                if (!value) {
                  return Promise.reject('Por favor, digite o valor do pagamento');
                }

                const valueClean = moneyMask.clean(value);
                if (valueClean <= 0) {
                  return Promise.reject('O valor do pagamento deve ser maior que 0');
                }

                const total = getTotal();

                if (valueClean > total / 100.0) {
                  return Promise.reject('O valor do pagamento não pode ser maior que o total');
                }

                const paymentsTotal = payments.reduce(
                  (acc: number, payment: any) => acc + moneyMask.clean(payment.value),
                  0
                );

                if (total !== Math.round(paymentsTotal * 100)) {
                  return Promise.reject('O valor total dos pagamentos deve ser igual ao valor total da compra.');
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            prefix="R$"
            name="value"
            onChange={(e) => {
              const fixed = moneyMask.apply(e.target.value);
              form.setFieldValue([...name, field.name, 'value'], fixed);
            }}
            onBlur={(e) => {
              let valueClean = moneyMask.clean(e.target.value);
              const total = getTotal() / 100.0;
              const totalPayments = payments.reduce((acc: number, payment: any, index: number) => {
                if (index === field.name) {
                  return acc;
                }

                return acc + moneyMask.clean(payment.value);
              }, 0);

              const restValue = total - totalPayments;

              if (valueClean > restValue) {
                valueClean = restValue;
              }

              const fixed = moneyMask.apply(valueClean);

              form.setFieldValue([...name, field.name, 'value'], fixed);
              form.validateFields([...name, field.name], {
                recursive: true,
                dirty: true,
              });
            }}
          />
        </Form.Item>
      )}

      {paymentMethod === 'credit_card' && !creditCardId && <CreditCard name={[field.name, 'creditCard']} />}
      {paymentMethod === 'credit_card' && creditCardId && <CreditCardResumed name={[field.name, 'creditCard']} />}
      {paymentMethod === 'boleto' && <Boleto />}
      {paymentMethod === 'pix' && <Pix />}
    </>
  );
};

export default PaymentMethod;
